/**
 * Tax Loss Harvesting Tool
 *
 * Identifies opportunities to sell assets at a loss for tax optimization
 * 100% READ-ONLY - No data modification
 *
 * Features:
 * - Scans portfolio for losing positions
 * - Calculates potential tax savings
 * - Suggests replacement assets (avoid wash sale rules)
 * - MiCA compliance ready
 *
 * IMPORTANT: This tool only provides suggestions. User must manually execute trades.
 */

import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../components/Icons';
import { usePortfolio } from '../hooks/usePortfolio';
import { usePositions } from '../hooks/usePositions';
import { useAuthStore } from '../store/authStore';
import { useCoinPrices } from '../hooks/queries/useCoinGecko';
import { StorageService } from '../services/storage';
import type { Token, Position, Prices } from '../types';

interface TaxLossOpportunity {
  tokenId: string;
  tokenName: string;
  symbol: string;
  totalQuantity: number;
  avgCostBasis: number;
  currentPrice: number;
  currentValue: number;
  unrealizedLoss: number;
  unrealizedLossPercent: number;
  potentialTaxSavings: number;
  suggestedReplacements: string[];
  riskLevel: 'low' | 'medium' | 'high';
}

export function TaxLossHarvesting() {
  const { isAuthenticated } = useAuthStore();
  // Auth gate at the wrapper level — see CongressTrading / TaxCenter for
  // the same refactor (rules of hooks, stable hook order).
  if (!isAuthenticated) {
    return <TaxLossHarvestingLoginGate />;
  }
  return <TaxLossHarvestingAuthenticated />;
}

function TaxLossHarvestingLoginGate() {
  const { t } = useTranslation();
  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '8px' }}>
        {t('tax.lossHarvesting.title', 'Tax Loss Harvesting')}
      </h1>
      <p style={{ color: 'var(--muted)', marginBottom: '24px' }}>
        {t('tax.lossHarvesting.subtitle', 'Optimisez vos impôts en identifiant les pertes latentes')}
      </p>

      <div style={{
        background: 'rgba(0, 0, 0, 0.25)',
        backdropFilter: 'blur(20px)',
        borderRadius: '16px',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        padding: '60px 20px',
        textAlign: 'center',
      }}>
        <div style={{
          width: '64px',
          height: '64px',
          borderRadius: '16px',
          background: 'rgba(139, 92, 246, 0.15)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 20px',
        }}>
          <Icons.Lock size={32} color="#8b5cf6" />
        </div>
        <h2 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '8px' }}>
          {t('tax.loginRequired', 'Connexion requise')}
        </h2>
        <p style={{ color: 'var(--muted)', fontSize: '14px', maxWidth: '300px', margin: '0 auto' }}>
          {t('tax.loginMessage', 'Connectez-vous pour accéder à cet outil de Tax Loss Harvesting.')}
        </p>
      </div>
    </div>
  );
}

function TaxLossHarvestingAuthenticated() {
  const { t } = useTranslation();

  // Hybrid mode: portfolio + positions hooks must run unconditionally on
  // every render of this component.
  const portfolioHook = usePortfolio();
  const positionsHook = usePositions(portfolioHook.activePortfolio?.id);

  const [tokens, setTokens] = useState<Token[]>([]);
  const [taxRate, setTaxRate] = useState(30); // Default 30% tax rate
  const [minLossThreshold, setMinLossThreshold] = useState(100); // Minimum $100 loss
  const [showDetails, setShowDetails] = useState<string | null>(null);

  // Load cached prices from localStorage (shared with Portfolio Dashboard)
  const [cachedPrices, setCachedPrices] = useState<Prices>({});
  useEffect(() => {
    const cachedData = StorageService.get<{ prices: Prices; timestamp: number }>('cached_prices');
    if (cachedData && cachedData.prices) {
      setCachedPrices(cachedData.prices);
    }
  }, []);

  // Fetch real-time prices from CoinGecko API (with React Query caching)
  const tokenIds = useMemo(() => tokens.map(t => t.id), [tokens]);
  const { data: apiPrices = {}, isLoading: pricesLoading } = useCoinPrices(tokenIds, {
    enabled: tokenIds.length > 0,
    refetchInterval: 60000, // Refresh every 60s
  });

  // Merge API prices with cached prices (prefer API, fallback to cache)
  const prices = useMemo(() => {
    // If API is loading and we have cache, use cache as fallback
    if (pricesLoading && Object.keys(cachedPrices).length > 0) {
      return { ...cachedPrices, ...apiPrices };
    }
    return apiPrices;
  }, [apiPrices, cachedPrices, pricesLoading]);

  // Sync tokens from hybrid hooks (READ-ONLY)
  useEffect(() => {
    if (portfolioHook.loading || positionsHook.loading) return;
    if (!portfolioHook.activePortfolio) return;

    // Group positions by tokenId
    const positionsByToken = new Map<string, Position[]>();
    positionsHook.positions.forEach((hybridPos) => {
      const tokenId = 'token_id' in hybridPos ? hybridPos.token_id : 'tokenId' in hybridPos ? hybridPos.tokenId : '';
      const legacyPosition: Position = {
        id: hybridPos.id,
        type: hybridPos.type as 'buy' | 'sell',
        date: hybridPos.date,
        quantity: hybridPos.quantity,
        price: hybridPos.price,
        fee: hybridPos.fee || undefined,
        note: hybridPos.note || undefined,
        is_swap: (hybridPos as any).is_swap,
        swap_chain_id: (hybridPos as any).swap_chain_id,
        original_cost_basis: (hybridPos as any).original_cost_basis,
      };

      const existing = positionsByToken.get(tokenId) || [];
      positionsByToken.set(tokenId, [...existing, legacyPosition]);
    });

    // Convert hybrid tokens to legacy Token format
    const legacyTokens: Token[] = portfolioHook.tokens.map((hybridToken) => {
      const tokenId = 'coin_id' in hybridToken ? hybridToken.coin_id : hybridToken.id;
      const displayName = 'symbol' in hybridToken ? hybridToken.symbol : 'name' in hybridToken ? hybridToken.name : tokenId;

      return {
        id: tokenId,
        displayName: (displayName || tokenId || 'UNKNOWN').toUpperCase(),
        positions: positionsByToken.get(tokenId) || [],
        note: 'notes' in hybridToken ? hybridToken.notes : undefined,
        tags: [],
        targetPrice: hybridToken.targetPrice || hybridToken.target_price || undefined,
      };
    });

    setTokens(legacyTokens);
  }, [
    portfolioHook.loading,
    portfolioHook.activePortfolio,
    portfolioHook.tokens,
    positionsHook.loading,
    positionsHook.positions,
  ]);

  // Replacement suggestions by category
  const replacementSuggestions: Record<string, string[]> = {
    'bitcoin': ['Ethereum', 'Litecoin', 'Bitcoin Cash'],
    'ethereum': ['Bitcoin', 'Cardano', 'Polkadot'],
    'solana': ['Avalanche', 'Polygon', 'Near'],
    'cardano': ['Polkadot', 'Ethereum', 'Algorand'],
    'default': ['Bitcoin', 'Ethereum', 'Stablecoin USDC']
  };

  // Calculate tax loss opportunities
  const opportunities = useMemo(() => {
    const opps: TaxLossOpportunity[] = [];

    tokens.forEach(token => {
      if (!token.positions || token.positions.length === 0) return;

      // Get current price from API
      const currentPrice = prices[token.id]?.usd || 0;
      if (currentPrice === 0) return; // Skip if no price data

      // Calculate average cost basis from buy positions only
      let totalCost = 0;
      let totalQuantity = 0;

      token.positions.forEach(position => {
        if (position.type === 'buy') {
          totalCost += position.price * position.quantity;
          totalQuantity += position.quantity;
        } else if (position.type === 'sell') {
          totalQuantity -= position.quantity;
        }
      });

      if (totalQuantity <= 0) return;

      const avgCostBasis = totalCost / totalQuantity;
      const currentValue = currentPrice * totalQuantity;
      const unrealizedLoss = currentValue - (avgCostBasis * totalQuantity);

      // Only include losing positions
      if (unrealizedLoss >= 0) return;

      // Apply minimum threshold
      if (Math.abs(unrealizedLoss) < minLossThreshold) return;

      const unrealizedLossPercent = (unrealizedLoss / totalCost) * 100;
      const potentialTaxSavings = Math.abs(unrealizedLoss) * (taxRate / 100);

      // Determine risk level based on loss percentage
      let riskLevel: 'low' | 'medium' | 'high' = 'low';
      if (Math.abs(unrealizedLossPercent) > 50) riskLevel = 'high';
      else if (Math.abs(unrealizedLossPercent) > 25) riskLevel = 'medium';

      // Get replacement suggestions
      const tokenLower = token.id.toLowerCase();
      const suggestedReplacements = replacementSuggestions[tokenLower] || replacementSuggestions.default;

      opps.push({
        tokenId: token.id,
        tokenName: token.displayName,
        symbol: token.displayName.toUpperCase(),
        totalQuantity,
        avgCostBasis,
        currentPrice,
        currentValue,
        unrealizedLoss,
        unrealizedLossPercent,
        potentialTaxSavings,
        suggestedReplacements,
        riskLevel
      });
    });

    // Sort by potential tax savings (highest first)
    return opps.sort((a, b) => b.potentialTaxSavings - a.potentialTaxSavings);
  }, [tokens, prices, taxRate, minLossThreshold]);

  // Calculate totals
  const totals = useMemo(() => {
    const totalLoss = opportunities.reduce((sum, opp) => sum + Math.abs(opp.unrealizedLoss), 0);
    const totalSavings = opportunities.reduce((sum, opp) => sum + opp.potentialTaxSavings, 0);

    return { totalLoss, totalSavings };
  }, [opportunities]);

  const fmt = (n: number, decimals = 2) => n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <Icons.TrendingDown size={32} color="var(--accent)" />
          Tax Loss Harvesting
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: '15px' }}>
          Optimize your taxes by strategically selling losing positions. MiCA compliant.
        </p>
      </div>

      {/* Disclaimer */}
      <div style={{
        padding: '16px',
        background: 'rgba(251, 191, 36, 0.1)',
        border: '1px solid rgba(251, 191, 36, 0.3)',
        borderRadius: '12px',
        marginBottom: '24px'
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <Icons.AlertTriangle size={20} color="#fbbf24" style={{ flexShrink: 0, marginTop: '2px' }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, marginBottom: '4px', color: '#fbbf24' }}>⚠️ Important Disclaimer</div>
            <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
              This tool provides suggestions only. Consult with a tax professional before making decisions.
              Be aware of wash sale rules (30-day rule). All trades must be executed manually.
            </p>
          </div>
        </div>
      </div>

      {/* Settings */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px' }}>Settings</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px', color: 'var(--text-secondary)' }}>
              Tax Rate (%)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              value={taxRate}
              onChange={(e) => setTaxRate(Number(e.target.value))}
              style={{
                width: '100%',
                padding: '10px 12px',
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                color: 'var(--text-primary)',
                fontSize: '14px'
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px', color: 'var(--text-secondary)' }}>
              Minimum Loss Threshold ($)
            </label>
            <input
              type="number"
              min="0"
              value={minLossThreshold}
              onChange={(e) => setMinLossThreshold(Number(e.target.value))}
              style={{
                width: '100%',
                padding: '10px 12px',
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                color: 'var(--text-primary)',
                fontSize: '14px'
              }}
            />
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <div className="card" style={{ padding: '20px' }}>
          <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px' }}>Opportunities Found</div>
          <div style={{ fontSize: '32px', fontWeight: 700, color: 'var(--accent)' }}>{opportunities.length}</div>
        </div>
        <div className="card" style={{ padding: '20px' }}>
          <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px' }}>Total Unrealized Loss</div>
          <div style={{ fontSize: '32px', fontWeight: 700, color: '#ef4444' }}>-${fmt(totals.totalLoss, 0)}</div>
        </div>
        <div className="card" style={{ padding: '20px' }}>
          <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px' }}>Potential Tax Savings</div>
          <div style={{ fontSize: '32px', fontWeight: 700, color: '#10b981' }}>${fmt(totals.totalSavings, 0)}</div>
        </div>
      </div>

      {/* Opportunities List */}
      {opportunities.length === 0 ? (
        <div className="card" style={{ padding: '60px 20px', textAlign: 'center' }}>
          <Icons.TrendingUp size={48} style={{ margin: '0 auto 16px', opacity: 0.3 }} />
          <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>No Tax Loss Opportunities Found</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
            All your positions are profitable or below the minimum loss threshold.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {opportunities.map(opp => (
            <div
              key={opp.tokenId}
              className="card"
              style={{
                padding: '20px',
                border: showDetails === opp.tokenId ? '1px solid var(--accent)' : undefined,
                transition: 'all 0.2s'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: showDetails === opp.tokenId ? '16px' : '0' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <h3 style={{ fontSize: '18px', fontWeight: 600 }}>{opp.tokenName}</h3>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      background: opp.riskLevel === 'high' ? 'rgba(239, 68, 68, 0.1)' :
                                 opp.riskLevel === 'medium' ? 'rgba(251, 191, 36, 0.1)' :
                                 'rgba(16, 185, 129, 0.1)',
                      color: opp.riskLevel === 'high' ? '#ef4444' :
                             opp.riskLevel === 'medium' ? '#fbbf24' :
                             '#10b981'
                    }}>
                      {opp.riskLevel} Risk
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '24px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                    <span>Loss: <strong style={{ color: '#ef4444' }}>-${fmt(Math.abs(opp.unrealizedLoss), 0)} ({fmt(Math.abs(opp.unrealizedLossPercent))}%)</strong></span>
                    <span>Tax Savings: <strong style={{ color: '#10b981' }}>${fmt(opp.potentialTaxSavings, 0)}</strong></span>
                  </div>
                </div>
                <button
                  onClick={() => setShowDetails(showDetails === opp.tokenId ? null : opp.tokenId)}
                  style={{
                    padding: '8px 16px',
                    background: 'var(--accent)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  {showDetails === opp.tokenId ? 'Hide' : 'Details'}
                  {showDetails === opp.tokenId ? <Icons.ChevronUp size={16} /> : <Icons.ChevronDown size={16} />}
                </button>
              </div>

              {/* Details */}
              {showDetails === opp.tokenId && (
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: '16px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '20px' }}>
                    <div>
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Quantity</div>
                      <div style={{ fontSize: '16px', fontWeight: 600 }}>{fmt(opp.totalQuantity, 4)} {opp.symbol}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Avg Cost Basis</div>
                      <div style={{ fontSize: '16px', fontWeight: 600 }}>${fmt(opp.avgCostBasis)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Current Price</div>
                      <div style={{ fontSize: '16px', fontWeight: 600 }}>${fmt(opp.currentPrice)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Current Value</div>
                      <div style={{ fontSize: '16px', fontWeight: 600 }}>${fmt(opp.currentValue, 0)}</div>
                    </div>
                  </div>

                  {/* Suggested Replacements */}
                  <div style={{ background: 'var(--bg-secondary)', padding: '16px', borderRadius: '8px' }}>
                    <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px' }}>🔄 Suggested Replacements (avoid wash sale)</h4>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {opp.suggestedReplacements.map(replacement => (
                        <span
                          key={replacement}
                          style={{
                            padding: '6px 12px',
                            background: 'var(--accent-light)',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: 500
                          }}
                        >
                          {replacement}
                        </span>
                      ))}
                    </div>
                    <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '12px' }}>
                      ⚠️ Wait 30 days after selling before repurchasing the same asset to avoid wash sale rules.
                    </p>
                  </div>

                  {/* Action Plan */}
                  <div style={{ marginTop: '16px', padding: '12px', background: 'rgba(139, 92, 246, 0.1)', borderRadius: '8px' }}>
                    <h4 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: 'var(--accent)' }}>📋 Recommended Action</h4>
                    <ol style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.6', paddingLeft: '20px' }}>
                      <li>Sell {fmt(opp.totalQuantity, 4)} {opp.symbol} at current market price (${fmt(opp.currentPrice)})</li>
                      <li>Realize loss of -${fmt(Math.abs(opp.unrealizedLoss), 0)} for tax deduction</li>
                      <li>Save approximately ${fmt(opp.potentialTaxSavings, 0)} in taxes</li>
                      <li>Wait 30 days or buy one of the suggested replacements</li>
                    </ol>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
